import React, { useState } from "react"
import { useGuias } from "../context/GuiasContext"
import {Building2, Search, Edit2, Trash2, Eye, FileCheck, Plus, ChevronDown} from 'lucide-react'
import toast from "react-hot-toast"

interface ListarEmpresasProps {
  onEdit: (id: string) => void
  onEmitirAlvara: (id: string) => void
}

const statusColor: Record<string, string> = {
  ativo: "bg-green-100 text-green-700",
  inativo: "bg-gray-100 text-gray-500",
  suspenso: "bg-red-100 text-red-600"
}

export default function ListarEmpresas({ onEdit, onEmitirAlvara }: ListarEmpresasProps) {
  const { empresas, deleteEmpresa } = useGuias()
  const [busca, setBusca] = useState("")
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)
  const [viewing, setViewing] = useState<string | null>(null)

  const filtradas = empresas.filter(e =>
    e.razaoSocial?.toLowerCase().includes(busca.toLowerCase()) ||
    e.nomeFantasia?.toLowerCase().includes(busca.toLowerCase()) ||
    e.cnpj?.includes(busca)
  )

  const handleDelete = async (id: string) => {
    await deleteEmpresa(id)
    setConfirmDelete(null)
  }

  const empresa = viewing ? empresas.find(e => e._id === viewing) : null

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="bg-[#004161] px-6 py-4 flex items-center gap-3">
        <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
          <Building2 className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <h2 className="text-white font-bold text-base">Empresas Cadastradas</h2>
          <p className="text-blue-200 text-xs">{empresas.length} empresa(s) no cadastro</p>
        </div>
      </div>

      {/* Busca */}
      <div className="p-4 border-b border-gray-100">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            value={busca} onChange={e => setBusca(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#004161]/30"
            placeholder="Buscar por nome, fantasia ou CNPJ..."
          />
        </div>
      </div>

      {/* Lista */}
      <div className="divide-y divide-gray-50">
        {filtradas.length === 0 ? (
          <div className="py-16 text-center">
            <Building2 className="w-12 h-12 text-gray-200 mx-auto mb-3" />
            <p className="text-gray-400 text-sm">Nenhuma empresa encontrada</p>
          </div>
        ) : (
          filtradas.map(emp => (
            <div key={emp._id} className="px-4 py-3 hover:bg-gray-50 transition-colors">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#004161]/10 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-5 h-5 text-[#004161]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold text-gray-800 text-sm truncate">{emp.razaoSocial}</p>
                    {emp.nomeFantasia && (
                      <span className="text-xs text-gray-400">({emp.nomeFantasia})</span>
                    )}
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor[emp.status || "ativo"] || "bg-gray-100 text-gray-500"}`}>
                      {emp.status || "ativo"}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-3 mt-1">
                    <span className="text-xs text-gray-500">{emp.cnpj}</span>
                    {emp.tipoEmpresa && <span className="text-xs text-gray-400">{emp.tipoEmpresa}</span>}
                    {emp.ramoAtividade && <span className="text-xs text-gray-400">{emp.ramoAtividade}</span>}
                    {emp.cidade && <span className="text-xs text-gray-400">{emp.cidade}/{emp.uf}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => setViewing(emp._id || null)}
                    className="p-1.5 text-gray-400 hover:text-[#004161] hover:bg-[#004161]/10 rounded-lg transition-colors" title="Visualizar">
                    <Eye className="w-4 h-4" />
                  </button>
                  <button onClick={() => onEdit(emp._id || "")}
                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Editar">
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => onEmitirAlvara(emp._id || "")}
                    className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors" title="Emitir Alvará">
                    <FileCheck className="w-4 h-4" />
                  </button>
                  <button onClick={() => setConfirmDelete(emp._id || null)}
                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Excluir">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal Visualizar */}
      {viewing && empresa && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="bg-[#004161] px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Building2 className="w-5 h-5 text-white" />
                <h3 className="text-white font-bold">Dados da Empresa</h3>
              </div>
              <button onClick={() => setViewing(null)} className="text-white/70 hover:text-white">✕</button>
            </div>
            <div className="p-6 space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-xs text-gray-400 font-semibold uppercase">Razão Social</p><p className="text-gray-800 font-medium">{empresa.razaoSocial}</p></div>
                <div><p className="text-xs text-gray-400 font-semibold uppercase">Nome Fantasia</p><p className="text-gray-800">{empresa.nomeFantasia || "—"}</p></div>
                <div><p className="text-xs text-gray-400 font-semibold uppercase">CNPJ</p><p className="text-gray-800">{empresa.cnpj}</p></div>
                <div><p className="text-xs text-gray-400 font-semibold uppercase">CPF</p><p className="text-gray-800">{empresa.cpf || "—"}</p></div>
                <div><p className="text-xs text-gray-400 font-semibold uppercase">Tipo</p><p className="text-gray-800">{empresa.tipoEmpresa || "—"}</p></div>
                <div><p className="text-xs text-gray-400 font-semibold uppercase">Ramo</p><p className="text-gray-800">{empresa.ramoAtividade || "—"}</p></div>
                <div><p className="text-xs text-gray-400 font-semibold uppercase">CNAE</p><p className="text-gray-800">{empresa.cnae || "—"}</p></div>
                <div><p className="text-xs text-gray-400 font-semibold uppercase">Área (m²)</p><p className="text-gray-800">{empresa.areaM2 || "—"}</p></div>
                <div><p className="text-xs text-gray-400 font-semibold uppercase">Insc. Municipal</p><p className="text-gray-800">{empresa.inscricaoMunicipal || "—"}</p></div>
                <div><p className="text-xs text-gray-400 font-semibold uppercase">Insc. Estadual</p><p className="text-gray-800">{empresa.inscricaoEstadual || "—"}</p></div>
                <div><p className="text-xs text-gray-400 font-semibold uppercase">Responsável</p><p className="text-gray-800">{empresa.responsavel || "—"}</p></div>
                <div><p className="text-xs text-gray-400 font-semibold uppercase">CPF Responsável</p><p className="text-gray-800">{empresa.cpfResponsavel || "—"}</p></div>
                <div className="col-span-2"><p className="text-xs text-gray-400 font-semibold uppercase">Endereço</p><p className="text-gray-800">{[empresa.endereco, empresa.numero, empresa.complemento, empresa.bairro, empresa.cidade, empresa.uf].filter(Boolean).join(", ")} — CEP: {empresa.cep || "—"}</p></div>
                <div><p className="text-xs text-gray-400 font-semibold uppercase">Telefone</p><p className="text-gray-800">{empresa.telefone || "—"}</p></div>
                <div><p className="text-xs text-gray-400 font-semibold uppercase">E-mail</p><p className="text-gray-800">{empresa.email || "—"}</p></div>
                {empresa.observacoes && (
                  <div className="col-span-2"><p className="text-xs text-gray-400 font-semibold uppercase">Observações</p><p className="text-gray-800">{empresa.observacoes}</p></div>
                )}
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => { setViewing(null); onEmitirAlvara(empresa._id || "") }}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 text-sm font-semibold">
                  <FileCheck className="w-4 h-4" /> Emitir Alvará
                </button>
                <button onClick={() => setViewing(null)}
                  className="px-5 py-2.5 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 text-sm">
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmar exclusão */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
            <Trash2 className="w-12 h-12 text-red-400 mx-auto mb-3" />
            <h3 className="font-bold text-gray-800 mb-2">Excluir Empresa?</h3>
            <p className="text-gray-500 text-sm mb-5">Esta ação não pode ser desfeita.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDelete(null)}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 text-sm">
                Cancelar
              </button>
              <button onClick={() => handleDelete(confirmDelete)}
                className="flex-1 py-2.5 bg-red-500 text-white rounded-xl hover:bg-red-600 text-sm font-semibold">
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
